import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Truck, Sparkles } from "lucide-react";
import { IMG } from "@/config";
import { PortalButton } from "@/components/PortalButton";
import { maskLine } from "@/components/Reveal";

const HEADLINE = ["Edmonton's One-Stop", "Wholesale Partner", "for Restaurants"];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.22]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.8]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={IMG.heroWarehouse} alt="Large organized commercial wholesale warehouse interior" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 z-0 bg-forest-deep" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-forest-deep via-forest-deep/30 to-transparent" />

      <div className="relative max-w-[1440px] w-full mx-auto px-5 md:px-10 pt-32 pb-14">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex rounded-2xl bg-white p-3 md:p-4 mb-6 shadow-hard-lg"
        >
          <img src="/brand_logo.png" alt="Brampton Cash & Carry" className="h-16 sm:h-20 lg:h-24 w-auto object-contain" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="inline-flex items-center gap-2 rounded-full bg-butter/15 border border-butter/30 px-4 py-1.5 mb-7 backdrop-blur"
        >
          <Sparkles className="h-3.5 w-3.5 text-butter-deep" />
          <span className="overline text-butter">Restaurant Foodservice Wholesale · Edmonton, AB</span>
        </motion.div>

        <h1 className="font-display text-[13vw] sm:text-6xl lg:text-[5.4rem] leading-[0.95] tracking-tight text-cream max-w-5xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span variants={maskLine} custom={i} initial="hidden" animate="show" className="block">
                {i === 1 ? <span className="text-butter-deep">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="text-cream/80 text-base md:text-lg max-w-2xl mt-7 leading-relaxed"
        >
          From food ingredients and restaurant essentials to packaging and cleaning supplies, Brampton Cash &amp; Carry
          gives food businesses access to more than <span className="text-butter font-semibold">500 wholesale products</span>,
          competitive pricing and reliable delivery across Edmonton and Alberta.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7 }}
          className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-3 mt-9"
        >
          <PortalButton variant="butter" testId="hero-open-account-btn">Open a Wholesale Account</PortalButton>
          <Link
            to="/product-categories"
            data-testid="hero-explore-btn"
            className="group inline-flex items-center gap-2 rounded-full border border-cream/40 text-cream px-6 py-3 text-sm font-semibold hover:bg-cream hover:text-forest transition-colors duration-300"
          >
            Explore Product Categories
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <span className="inline-flex items-center gap-2 text-cream/70 text-sm sm:ml-2">
            <Truck className="h-4 w-4 text-butter-deep" />
            Sign up today &amp; start receiving orders as early as the next business day.
          </span>
        </motion.div>
      </div>
    </section>
  );
}
